import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  )
}

type TooltipTriggerRef = React.ElementRef<typeof TooltipPrimitive.Trigger>;
type TooltipTriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;

const TooltipTrigger = React.forwardRef<TooltipTriggerRef, TooltipTriggerProps>(
  ({ ...props }, ref) => (
    <TooltipPrimitive.Trigger ref={ref} data-slot="tooltip-trigger" {...props} />
  )
);
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

type TooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Content> & {
  arrowClassName?: string;
  portalContainer?: HTMLElement | null;
};

type TooltipContentRef = React.ElementRef<typeof TooltipPrimitive.Content>;

const TooltipContent = React.forwardRef<TooltipContentRef, TooltipContentProps>(
  (
    {
      className,
      sideOffset = 0,
      children,
      arrowClassName,
      portalContainer,
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Portal container={portalContainer ?? undefined}>
      <TooltipPrimitive.Content
        ref={ref}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-foreground text-background animate-animate-in animate-fade-in-0 animate-zoom-in-95 data-[state=closed]:animate-animate-out data-[state=closed]:animate-fade-out-0 data-[state=closed]:animate-zoom-out-95 data-[side=bottom]:animate-slide-in-from-top-2 data-[side=left]:animate-slide-in-from-right-2 data-[side=right]:animate-slide-in-from-left-2 data-[side=top]:animate-slide-in-from-bottom-2 z-[99999990] w-fit max-w-[calc(100vw-24px)] sm:max-w-[420px] origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-left text-xs whitespace-normal break-words shadow-md",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            "fill-foreground text-foreground z-[99999991] h-3 w-3 drop-shadow-sm",
            arrowClassName
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
